import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Box,
  CircularProgress,
  Alert,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import api from "src/app/api";

interface SimulateAlertModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  moduleId?: string;
  moduleType?: string;
  moduleName?: string;
}

interface AlertRule {
  [key: string]: {
    min: number;
    max: number;
    warning?: {
      instructionName: string;
      instructions: string[];
    };
    error?: {
      instructionName: string;
      instructions: string[];
    };
  };
}

interface IndicatorOption {
  instructionName: string;
  indicatorKey: string;
}


export const SimulateAlertModal: React.FC<SimulateAlertModalProps> = ({
  open,
  onClose,
  onSuccess,
  moduleId,
  moduleType = "Engine",
  moduleName,
}) => {
  const [rules, setRules] = useState<AlertRule | null>(null);
  const [indicators, setIndicators] = useState<IndicatorOption[]>([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [formData, setFormData] = useState({
    indicator_name: "", // stores instructionName, not key
    severity: "", // "warning" or "alert"
    cylinder: "", // optional: cylinder number (1, 2, 3, etc.)
  });

  // Indicators that support per-cylinder alerts
  const cylinderSupportedIndicators = [
    "Compression",
    "InjectionCondition",
    "Misfiring",
    "Bearing",
    "BearingBis",
  ];

  const selectedIndicatorKey = indicators.find(
    (ind) => ind.instructionName === formData.indicator_name
  )?.indicatorKey;

  const supportsCylinder = selectedIndicatorKey && cylinderSupportedIndicators.includes(selectedIndicatorKey);

  interface EmaRow { ema24h: number; ema7d: number; pctDev: number; }
  const [emaPreview, setEmaPreview] = useState<{
    warning: EmaRow | null;
    alert: EmaRow | null;
  }>({ warning: null, alert: null });

  const calcEmaRow = (severity: "warning" | "alert", min: number, max: number, isPowerLoss: boolean): EmaRow => {
    let ema24h: number;
    let ema7d: number;
    if (isPowerLoss) {
      ema24h = severity === "warning" ? max - 0.3 : max + 0.5;
      ema7d  = severity === "warning" ? min + 0.2 : min;
    } else {
      // For percentage-based indicators
      const mid = (min + max) / 2;
      if (severity === "warning") {
        ema24h = mid - 5;
        ema7d = mid + 5;
      } else {
        // alert
        ema24h = min / 2;
        ema7d = mid;
      }
    }
    const pctDev = ema7d !== 0 ? 100 * ((ema24h - ema7d) / ema7d) : 0;
    return { ema24h: Math.round(ema24h * 100) / 100, ema7d: Math.round(ema7d * 100) / 100, pctDev: Math.round(pctDev * 100) / 100 };
  };

  // Recompute EMA preview whenever indicator changes
  useEffect(() => {
    if (!formData.indicator_name || !rules) {
      setEmaPreview({ warning: null, alert: null });
      return;
    }
    const selectedIndicator = indicators.find(
      (ind) => ind.instructionName === formData.indicator_name
    );
    if (!selectedIndicator) return;
    const rule = rules[selectedIndicator.indicatorKey];
    if (!rule) return;
    const isPowerLoss = selectedIndicator.indicatorKey === "PowerLoss";
    setEmaPreview({
      warning: rule.warning ? calcEmaRow("warning", rule.min, rule.max, isPowerLoss) : null,
      alert:   rule.error   ? calcEmaRow("alert",   rule.min, rule.max, isPowerLoss) : null,
    });
  }, [formData.indicator_name, rules, indicators]);

  // Fetch alert rules on modal open
  useEffect(() => {
    if (open && moduleType) {
      fetchAlertRules();
    }
  }, [open, moduleType]);

  const fetchAlertRules = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get("/alerts/getAlertRules.php", {
        params: {
          module_type: moduleType,
        },
      });

      if (response.data?.Status === "Success" && response.data?.data?.rules) {
        const rulesData = response.data.data.rules;
        setRules(rulesData);

        // Extract unique instructionNames with their indicator keys
        const indicatorMap: Record<string, string> = {}; // instructionName -> indicatorKey
        const processedInstructionNames = new Set<string>();

        Object.entries(rulesData).forEach(([indicatorKey, rule]: [string, any]) => {
          // Get instructionName from warning or error section
          let instructionName = null;
          if (rule.warning?.instructionName) {
            instructionName = rule.warning.instructionName;
          } else if (rule.error?.instructionName) {
            instructionName = rule.error.instructionName;
          }

          // Only add if not already processed (deduplication)
          if (instructionName && !processedInstructionNames.has(instructionName)) {
            processedInstructionNames.add(instructionName);
            indicatorMap[instructionName] = indicatorKey;
          }
        });

        // Create indicator options sorted by instructionName
        const indicatorOptions: IndicatorOption[] = Array.from(
          processedInstructionNames
        ).map((instructionName) => ({
          instructionName,
          indicatorKey: indicatorMap[instructionName],
        }));

        indicatorOptions.sort((a, b) =>
          a.instructionName.localeCompare(b.instructionName)
        );

        setIndicators(indicatorOptions);
      } else {
        throw new Error("Invalid response format");
      }
    } catch (err: any) {
      setError("Failed to load alert rules");
      console.error("Error fetching alert rules:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleFormChange = (field: string, value: any) => {
    setFormData((prev) => ({
      ...prev,
      [field]: value,
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.indicator_name) {
      setError("Please select an indicator");
      return false;
    }
    if (!formData.severity) {
      setError("Please select a severity level (Warning or Alert)");
      return false;
    }
    return true;
  };

  const handleSimulateAlert = async () => {
    setError(null);

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);

    try {
      // Find the indicator key from the selected instructionName
      const selectedIndicator = indicators.find(
        (ind) => ind.instructionName === formData.indicator_name
      );

      if (!selectedIndicator) {
        throw new Error("Selected indicator not found");
      }

      const payload: any = {
        module_id: moduleId,
        indicator_name: selectedIndicator.indicatorKey, // Send the indicator key, not instructionName
        severity: formData.severity, // "warning" or "alert"
      };

      // Add cylinder if provided and applicable
      if (supportsCylinder && formData.cylinder) {
        payload.cylinder = parseInt(formData.cylinder, 10);
      }

      // Call the unified alert API with type=simulate
      const response = await api.post(
        "/alerts/engine_alerts/run_engine_alerts.php?type=simulate",
        payload
      );

      console.log("Simulate alert response:", response.data);

      if (response.data?.Status === "Success") {
        const severity = response.data?.data?.severity || formData.severity;
        const instructionName = response.data?.data?.instructionName || '';
        enqueueSnackbar(
          `Alert simulated successfully! Severity: ${severity}${instructionName ? ' - ' + instructionName : ''}`,
          {
            variant: "success",
          }
        );

        // Reset form
        setFormData({
          indicator_name: "",
          severity: "",
          cylinder: "",
        });
        setEmaPreview({ warning: null, alert: null });

        // Call onSuccess to trigger alert refresh in parent
        if (onSuccess) {
          onSuccess();
        }

        // Close modal after short delay to ensure callback completes
        setTimeout(() => {
          onClose();
        }, 100);
      } else {
        setError(response.data?.Message || "Failed to simulate alert");
      }
    } catch (err: any) {
      const errorMsg =
        err.response?.data?.Message ||
        err.message ||
        "Failed to simulate alert";
      setError(errorMsg);
      enqueueSnackbar(errorMsg, { variant: "error" });
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setFormData({
      indicator_name: "",
      severity: "",
      cylinder: "",
    });
    setEmaPreview({ warning: null, alert: null });
    setError(null);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>Simulate Alert</DialogTitle>
      <DialogContent sx={{ pt: 2 }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
            {error && <Alert severity="error">{error}</Alert>}

            {/* Module Display (Non-editable) */}
            <TextField
              label="Module"
              value={moduleName || ""}
              fullWidth
              disabled
              variant="outlined"
            />

            {/* Indicator Selector */}
            <FormControl fullWidth>
              <InputLabel>Indicator</InputLabel>
              <Select
                value={formData.indicator_name}
                label="Indicator"
                onChange={(e) =>
                  handleFormChange("indicator_name", e.target.value)
                }
                disabled={submitting || !indicators.length}
              >
                {indicators.map((indicator) => (
                  <MenuItem key={indicator.indicatorKey} value={indicator.instructionName}>
                    {indicator.instructionName}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {/* Severity Selector */}
            <FormControl fullWidth disabled={!formData.indicator_name}>
              <InputLabel>Severity Level</InputLabel>
              <Select
                value={formData.severity}
                label="Severity Level"
                onChange={(e) => handleFormChange("severity", e.target.value)}
                disabled={!formData.indicator_name || submitting}
              >
                <MenuItem value="warning">⚠️ Warning</MenuItem>
                <MenuItem value="alert">🔴 Alert</MenuItem>
              </Select>
            </FormControl>

            {/* Cylinder Selector (only for cylinder-supported indicators) */}
            {supportsCylinder && (
              <TextField
                label="Cylinder Number"
                type="number"
                value={formData.cylinder}
                onChange={(e) => handleFormChange("cylinder", e.target.value)}
                placeholder="e.g., 1, 2, 3..."
                inputProps={{ min: "1", step: "1" }}
                helperText="Enter cylinder number for cylinder-specific alert"
                disabled={submitting}
              />
            )}

            {/* EMA Preview — shown once indicator is selected */}
            {(emaPreview.warning || emaPreview.alert) && (
              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 1,
                  overflow: "hidden",
                }}
              >
                {/* Header row */}
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "90px 1fr 1fr 1fr",
                    gap: 0,
                    bgcolor: "grey.100",
                    px: 1.5,
                    py: 0.75,
                    borderBottom: "1px solid",
                    borderColor: "divider",
                  }}
                >
                  {["Severity", "EMA 24h", "EMA 7d", "% Deviation"].map((h) => (
                    <Box key={h} sx={{ fontSize: 12, fontWeight: 600, color: "text.secondary" }}>{h}</Box>
                  ))}
                </Box>

                {/* Warning row */}
                {emaPreview.warning && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "90px 1fr 1fr 1fr",
                      gap: 0,
                      px: 1.5,
                      py: 1,
                      bgcolor: formData.severity === "warning" ? "warning.50" : "transparent",
                      borderBottom: emaPreview.alert ? "1px solid" : "none",
                      borderColor: "divider",
                    }}
                  >
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: "warning.dark", display: "flex", alignItems: "center" }}>⚠️ Warning</Box>
                    <Box sx={{ fontSize: 13, color: "text.primary" }}>{emaPreview.warning.ema24h.toFixed(2)}</Box>
                    <Box sx={{ fontSize: 13, color: "text.primary" }}>{emaPreview.warning.ema7d.toFixed(2)}</Box>
                    <Box sx={{ fontSize: 13, color: "text.primary" }}>{emaPreview.warning.pctDev.toFixed(2)}%</Box>
                  </Box>
                )}

                {/* Alert row */}
                {emaPreview.alert && (
                  <Box
                    sx={{
                      display: "grid",
                      gridTemplateColumns: "90px 1fr 1fr 1fr",
                      gap: 0,
                      px: 1.5,
                      py: 1,
                      bgcolor: formData.severity === "alert" ? "error.50" : "transparent",
                    }}
                  >
                    <Box sx={{ fontSize: 13, fontWeight: 600, color: "error.dark", display: "flex", alignItems: "center" }}>🔴 Alert</Box>
                    <Box sx={{ fontSize: 13, color: "text.primary" }}>{emaPreview.alert.ema24h.toFixed(2)}</Box>
                    <Box sx={{ fontSize: 13, color: "text.primary" }}>{emaPreview.alert.ema7d.toFixed(2)}</Box>
                    <Box sx={{ fontSize: 13, color: "text.primary" }}>{emaPreview.alert.pctDev.toFixed(2)}%</Box>
                  </Box>
                )}
              </Box>
            )}

            {/* Info Box */}
            <Alert severity="info">
              <strong>How it works:</strong> Select an indicator and severity
              level. The system will create an alert using predefined EMA values
              for the selected condition.
            </Alert>
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={submitting}>
          Cancel
        </Button>
        <Button
          onClick={handleSimulateAlert}
          variant="contained"
          disabled={submitting || !formData.indicator_name || !formData.severity}
        >
          {submitting ? <CircularProgress size={24} /> : "Simulate Alert"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SimulateAlertModal;
